import { Box, CircularProgress, IconButton, IconButtonProps } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconFilledStar } from "src/components/icons-mui/filled-star";
import { IconOutlineStar } from "src/components/icons-mui/outline-star";
import { Button, type ButtonProps } from "src/components-mui/base/button/button";
import { useWatchlistToggle } from "src/components-mui/common/buttons/watchlist-button/use-watchlist-toggle";

export interface IWatchlistButtonProps extends Omit<ButtonProps, "loading" | "onClick"> {
    wikifolioId: string;
    wikifolioShortDescription: string;
    isOnWatchlist?: boolean;
    omitTitle?: boolean;
    addedToWatchlistMessage?: string;
    removedFromWatchlistMessage?: string;
}

export const WatchlistButton = ({
    wikifolioId,
    wikifolioShortDescription,
    isOnWatchlist: isOnWatchlistFromServer,
    omitTitle = false,
    addedToWatchlistMessage,
    removedFromWatchlistMessage,
    size = "medium",
    disabled,
    ...buttonProps
}: IWatchlistButtonProps) => {
    const { t } = useTranslation("common");

    const { isOnWatchlist, loading, onClick, dialog } = useWatchlistToggle({
        wikifolioId,
        wikifolioShortDescription,
        isOnWatchlist: isOnWatchlistFromServer,
        addedToWatchlistMessage,
        removedFromWatchlistMessage,
    });

    const IconStar = isOnWatchlist ? IconFilledStar : IconOutlineStar;
    const text = isOnWatchlist ? t("remove") : t("watchlist");

    // Icon is 16px for extraSmall, 24px for all other sizes
    const iconSize = size === "extraSmall" ? "16px" : "24px";

    return (
        <>
            {omitTitle ? (
                <IconButton
                    variant="outlined"
                    size={size as IconButtonProps["size"]}
                    data-gtm-wikifolio-short-description={wikifolioShortDescription}
                    className={isOnWatchlist ? undefined : "gtm-watchlist__add"}
                    aria-label={t("watchlist")}
                    onClick={onClick}
                    disabled={loading || disabled}
                    {...(buttonProps as IconButtonProps)}
                >
                    <IconStar style={{ fontSize: iconSize }} sx={{ visibility: loading ? "hidden" : "visible" }} />
                    {loading && (
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex" }}>
                            <CircularProgress size={16} color="inherit" />
                        </Box>
                    )}
                </IconButton>
            ) : (
                <Button
                    variant="outlined"
                    size={size}
                    data-gtm-wikifolio-short-description={wikifolioShortDescription}
                    className={isOnWatchlist ? undefined : "gtm-watchlist__add"}
                    aria-label={t("watchlist")}
                    disabled={loading || disabled}
                    onClick={onClick}
                    sx={{ position: "relative" }}
                    {...buttonProps}
                >
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", visibility: loading ? "hidden" : "visible" }}>
                        <IconStar style={{ fontSize: iconSize }} sx={{ marginRight: 1 }} />
                        {text}
                    </Box>
                    {loading && (
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex" }}>
                            <CircularProgress size={16} color="inherit" />
                        </Box>
                    )}
                </Button>
            )}
            {dialog}
        </>
    );
};
